/**
 * Gmail-style multi-recipient field: each address becomes a removable chip and
 * the caret stays in a free-text input at the end of the row.
 *
 * Commit rules (mirroring Gmail so agents don't have to learn anything new):
 *   - Enter / Tab / "," / ";" commit the typed text as a chip
 *   - blurring the field commits whatever is left in it
 *   - pasting a list splits on commas, semicolons and whitespace
 *   - Backspace on an empty input removes the last chip
 *   - `Name <a@b.com>` is unwrapped to the bare address
 *
 * Addresses that fail validation are still added — as red chips — rather than
 * being silently dropped, so a bad paste is visible and fixable. Callers gate
 * sending on `hasInvalidRecipient(value)`.
 */
import { useRef, useState, type ClipboardEvent, type JSX, type KeyboardEvent } from 'react'
import { X } from 'lucide-react'
import { cn } from '../../utils/cn.js'

/** Pragmatic local@domain.tld check — full RFC 5322 is overkill here. */
const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/

/** True when `value` looks like a deliverable email address. */
export function isValidEmail(value: string): boolean {
  return EMAIL_REGEX.test(value.trim())
}

/** True when any entry in `list` would be rejected by the mail server. */
export function hasInvalidRecipient(list: string[]): boolean {
  return list.some((v) => !isValidEmail(v))
}

/** `"Support Team <help@acme.com>"` → `"help@acme.com"`; plain input passes through. */
export function extractAddress(raw: string): string {
  const angled = /<([^>]+)>/.exec(raw)
  return (angled?.[1] ?? raw).trim().replace(/^[,;]+|[,;]+$/g, '')
}

export interface EmailChipsInputProps {
  value: string[]
  onChange: (next: string[]) => void
  placeholder?: string | undefined
  /** Focus the text input on mount (used by Forward, where To starts empty). */
  autoFocus?: boolean | undefined
  disabled?: boolean | undefined
  ariaLabel?: string | undefined
  /** Compact borderless styling for the inline (Gmail-like) composer. */
  variant?: 'bordered' | 'inline' | undefined
}

export default function EmailChipsInput(props: EmailChipsInputProps): JSX.Element {
  const { value, onChange, disabled = false, variant = 'bordered' } = props
  const [draft, setDraft] = useState('')
  const inputRef = useRef<HTMLInputElement>(null)

  /** Add `candidates`, skipping blanks and addresses already on the list. */
  const commit = (candidates: string[]): void => {
    const additions: string[] = []
    for (const candidate of candidates) {
      const address = extractAddress(candidate)
      if (!address) continue
      const exists = [...value, ...additions].some((v) => v.toLowerCase() === address.toLowerCase())
      if (exists) continue
      additions.push(address)
    }
    if (additions.length > 0) onChange([...value, ...additions])
  }

  const commitDraft = (): void => {
    if (!draft.trim()) return
    commit([draft])
    setDraft('')
  }

  const handleKeyDown = (e: KeyboardEvent<HTMLInputElement>): void => {
    if (e.key === 'Enter' || e.key === ',' || e.key === ';') {
      e.preventDefault()
      commitDraft()
      return
    }
    if (e.key === 'Tab' && draft.trim()) {
      // Don't trap focus — commit, then let the browser move on.
      commitDraft()
      return
    }
    if (e.key === 'Backspace' && draft === '' && value.length > 0) {
      e.preventDefault()
      onChange(value.slice(0, -1))
    }
  }

  const handlePaste = (e: ClipboardEvent<HTMLInputElement>): void => {
    const text = e.clipboardData.getData('text')
    if (!/[,;\s]/.test(text)) return // single address — let the input handle it
    e.preventDefault()
    commit(text.split(/[,;\s]+/))
    setDraft('')
  }

  return (
    <div
      onClick={() => inputRef.current?.focus()}
      className={cn(
        'flex w-full flex-wrap items-center gap-1.5 text-sm transition-all',
        variant === 'bordered'
          ? 'rounded-[3px] border border-gray-200 bg-gray-50 px-2 py-1.5 focus-within:border-[#DCE7F8] focus-within:bg-white focus-within:ring-2 focus-within:ring-[#EEF4FF]'
          : 'py-1',
        disabled && 'cursor-not-allowed opacity-60',
      )}
    >
      {value.map((address) => {
        const invalid = !isValidEmail(address)
        return (
          <span
            key={address}
            title={invalid ? `${address} is not a valid email address` : address}
            className={cn(
              'inline-flex max-w-full items-center gap-1 rounded-[3px] px-2 py-0.5 text-xs',
              invalid
                ? 'border border-red-200 bg-red-50 text-red-600'
                : 'border border-gray-200 bg-white text-gray-700',
            )}
          >
            <span className="truncate">{address}</span>
            {!disabled && (
              <button
                type="button"
                aria-label={`Remove ${address}`}
                onClick={(e) => {
                  e.stopPropagation()
                  onChange(value.filter((v) => v !== address))
                }}
                className="shrink-0 text-gray-400 transition-colors hover:text-gray-700"
              >
                <X className="h-3 w-3" />
              </button>
            )}
          </span>
        )
      })}
      <input
        ref={inputRef}
        value={draft}
        disabled={disabled}
        autoFocus={props.autoFocus ?? false}
        aria-label={props.ariaLabel ?? 'Recipients'}
        onChange={(e) => setDraft(e.target.value)}
        onKeyDown={handleKeyDown}
        onPaste={handlePaste}
        onBlur={commitDraft}
        placeholder={value.length === 0 ? (props.placeholder ?? 'Add recipients') : ''}
        className="min-w-[8rem] flex-1 border-none bg-transparent px-1 py-0.5 text-sm text-gray-800 outline-none placeholder:text-gray-400"
      />
    </div>
  )
}
